/**
 * Feature 4 (2026-05-28) - MyTasksPage.
 *
 * For Maker: tasks where the user is creator OR assignee.
 * For Admin: all alive tasks system-wide. Mirrors the dashboard "All
 * Tasks" card scope (which calls /my-counts and gets scope='all' for
 * admin).
 *
 * Status filter is a row of always-visible toggle pills (green when
 * active, grey when not). The 4th "Deleted" pill is admin/compliance
 * only — it toggles include_deleted.
 */
import {
  Anchor, Badge, Button, Chip, Container, Group, Pagination, Stack, Table, Text,
  Title, Tooltip,
} from '@mantine/core';
import { IconFilterOff, IconPlus } from '@tabler/icons-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useCurrentUser } from '../hooks/useAuth';
import { useMyAgreementTasks } from '../hooks/useAgreementTasks';
import { useStaffOptions } from '../hooks/useAgreementInteractions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  ColumnDateRangeFilter, ColumnMultiSelectFilter, ColumnTextFilter, hasActiveFilters,
} from '../components/ColumnFilters';
import type { AgreementTask, AgreementTaskStatus } from '../api/types';
import { AGREEMENT_TASK_STATUS_LABELS } from '../api/types';
import { EntityFriendlyLabel } from '../components/EntityFriendlyLabel';
import { TaskFormModal } from '../components/TaskFormModal';
import { TaskRowActions } from '../components/TaskRowActions';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { AgreementPicker } from '../components/EntityPicker';
import { isOverdue, type TaskSortKey } from '../utils/taskList';
import { URGENCY_OPTIONS, urgencyColor, urgencyLabel } from '../utils/taskUrgency';
import { SortableTh, nextSort, sortParam, type SortState } from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';

const PAGE_SIZE = 50;

const STATUS_COLOR: Record<AgreementTaskStatus, string> = {
  OPEN: 'yellow',
  DONE: 'green',
  CANCELLED: 'gray',
};

type FilterChip = AgreementTaskStatus | 'DELETED';

export function MyTasksPage() {
  useDocumentTitle('My Tasks');
  const { user } = useCurrentUser();
  const isAdmin = user?.role === 'admin';
  const isChecker = user?.role === 'admin' || user?.role === 'compliance';

  const [selected, setSelected] = React.useState<FilterChip[]>(['OPEN']);
  // P41 phase 2 (2026-10-15): every header sorts, server-side across pages; null = the
  // existing order (deadline, earliest first). Overdue open tasks always lead (P14).
  const [sort, setSortState] = React.useState<SortState<TaskSortKey> | null>(null);
  const [page, setPage] = React.useState(1);
  function onSort(k: TaskSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  // ── #47 (2026-06-12): per-column predicate filters (C-132), server-side.
  // The status chips above stay as the POPULATION selector; the Status
  // column gets NO header filter (one axis, one control).
  const [descQ, setDescQ] = React.useState('');
  const [deadlineFrom, setDeadlineFrom] = React.useState('');
  const [deadlineTo, setDeadlineTo] = React.useState('');
  // C-132 Amendment 3 (2026-06-12, Pedro): people-pickers are multi too.
  const [assigneeF, setAssigneeF] = React.useState<string[]>([]);
  // #47 follow-up (Pedro's catch, same gap class as the policy Accounts
  // column): the Agreement relationship column is filterable too, and the
  // Created column filters by creator (Pedro 2026-06-12).
  const [createdByF, setCreatedByF] = React.useState<string[]>([]);
  // P14: urgency column filter (multi; server-side).
  const [urgencyF, setUrgencyF] = React.useState<string[]>([]);
  // P42: RM column filter — the related agreement's Primary Named RM(s). Multi; server-side.
  const [rmF, setRmF] = React.useState<string[]>([]);
  // #63: unified "Related to" combobox filter. null/'__all__' = all,
  // '__general__' = General (no-agreement), a UUID = that exact agreement.
  const [relatedTo, setRelatedTo] = React.useState<string | null>(null);
  const relActive = !!relatedTo && relatedTo !== '__all__';
  const relGeneral = relatedTo === '__general__';
  const relAgreementId =
    relatedTo && relatedTo !== '__general__' && relatedTo !== '__all__'
      ? relatedTo : undefined;

  const filtersActive = relActive || hasActiveFilters({
    descQ, deadlineFrom, deadlineTo, assigneeF, createdByF, urgencyF, rmF,
  });
  function clearFilters() {
    setDescQ(''); setDeadlineFrom(''); setDeadlineTo(''); setAssigneeF([]);
    setCreatedByF([]); setRelatedTo(null); setUrgencyF([]); setRmF([]);
  }

  const staffQ = useStaffOptions();
  const assigneeOptions = (Array.isArray(staffQ.data) ? staffQ.data : [])
    .map((u) => ({ value: u.id, label: u.display_name }));

  const statuses = selected.filter((s) => s !== 'DELETED') as AgreementTaskStatus[];
  const includeDeleted = isChecker && selected.includes('DELETED');

  const q = useMyAgreementTasks({
    statuses: statuses.length > 0 ? statuses : undefined,
    // Admin: list all tasks (matches the dashboard card scope='all').
    // Maker / compliance: only tasks where they are creator or assignee.
    mine: !isAdmin,
    include_deleted: includeDeleted,
    q: descQ || undefined,
    assignee_user_id: assigneeF.length > 0 ? assigneeF : undefined,
    created_by_user_id: createdByF.length > 0 ? createdByF : undefined,
    deadline_from: deadlineFrom || undefined,
    deadline_to: deadlineTo || undefined,
    general: relGeneral || undefined,
    agreement_id: relAgreementId,
    urgency: urgencyF.length > 0 ? urgencyF.map(Number) : undefined,
    rm_user_id: rmF.length > 0 ? rmF : undefined,
    sort: sortParam(sort),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const items = q.data?.items ?? [];
  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE)) : 1;
  // Any change to the population or a column filter returns to page 1.
  const filterKey = JSON.stringify([selected, descQ, deadlineFrom, deadlineTo, assigneeF,
    createdByF, urgencyF, rmF, relatedTo]);
  React.useEffect(() => { setPage(1); }, [filterKey]);

  // #63: the hub is now a manage surface (create + per-row actions), not a
  // read-only list. The create modal carries no fixed agreement, so it shows
  // the Agreement picker (with a "General" option once Inc 4 lands).
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AgreementTask | null>(null);
  function openCreate() { setEditing(null); setModalOpen(true); }
  function openEdit(t: AgreementTask) { setEditing(t); setModalOpen(true); }

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Title order={2}>{isAdmin ? 'All Tasks' : 'My Tasks'}</Title>
            <Text c="dimmed" size="sm">
              {isAdmin
                ? 'Every alive task across the system. Click an Agreement to open its full task tab.'
                : 'Tasks where you are either the creator or the assignee. Click an Agreement to open its full task tab.'}
            </Text>
          </Stack>
          <Button leftSection={<IconPlus size={14} />} onClick={openCreate}>
            New task
          </Button>
        </Group>

        <Group gap="lg" wrap="wrap" align="flex-end">
          <Stack gap={4}>
            <Text size="sm" fw={500}>Status</Text>
            <Chip.Group multiple value={selected as string[]}
              onChange={(v) => setSelected(v as FilterChip[])}>
              <Group gap="xs">
                {/* P47: each status explains itself on hover. */}
                <StatusExplained entity="task" status="OPEN"><Chip value="OPEN" color="green">Open</Chip></StatusExplained>
                <StatusExplained entity="task" status="DONE"><Chip value="DONE" color="green">Done</Chip></StatusExplained>
                <StatusExplained entity="task" status="CANCELLED"><Chip value="CANCELLED" color="green">Cancelled</Chip></StatusExplained>
                {isChecker && (
                  <StatusExplained entity="task" status="DELETED"><Chip value="DELETED" color="green">Deleted</Chip></StatusExplained>
                )}
              </Group>
            </Chip.Group>
          </Stack>
        </Group>

        {q.isLoading && <Text c="dimmed" size="sm">Loading…</Text>}
        {/* #47 / C-132 two-tier empty state: with no filters active an
            empty result hides the table; with filters active the table
            (and its filter row) must STAY so the user can clear them. */}
        {!q.isLoading && items.length === 0 && !filtersActive && (
          <Text c="dimmed" size="sm">
            No tasks for the current filter.
          </Text>
        )}

        {(items.length > 0 || filtersActive) && (
          <Table withTableBorder striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                {/* P41 phase 2 (2026-10-15): every header sorts (server-side, across
                    pages); overdue open tasks stay on top under every sort. */}
                <SortableTh label="Deadline" col="deadline" sort={sort} onSort={onSort} w={130}
                  filtered={deadlineFrom !== '' || deadlineTo !== ''} />
                <SortableTh label="Urgency" col="urgency" sort={sort} onSort={onSort} w={120}
                  filtered={urgencyF.length > 0} />
                <SortableTh label="Description" col="description" sort={sort} onSort={onSort}
                  filtered={!!descQ} />
                <SortableTh label="Related to" col="related" sort={sort} onSort={onSort} w={220}
                  filtered={relActive} />
                <SortableTh label="RM" col="rm" sort={sort} onSort={onSort} w={170}
                  filtered={rmF.length > 0} tooltip="The related agreement's Primary Named RM(s)" />
                <SortableTh label="Assignee" col="assignee" sort={sort} onSort={onSort} w={180}
                  filtered={assigneeF.length > 0} />
                <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={120} />
                <SortableTh label="Created" col="created" sort={sort} onSort={onSort} w={175}
                  filtered={createdByF.length > 0} />
                <Table.Th w={140}>Actions</Table.Th>
              </Table.Tr>
              {/* #47: per-column filter row (C-132). Status is owned by
                  the chips above; Created filters by creator (Pedro
                  2026-06-12), not by date — Deadline owns the date axis. */}
              <Table.Tr>
                <Table.Th>
                  <ColumnDateRangeFilter from={deadlineFrom} to={deadlineTo}
                    onChange={(f, t) => { setDeadlineFrom(f); setDeadlineTo(t); }}
                    ariaLabelPrefix="Deadline" />
                </Table.Th>
                <Table.Th>
                  <ColumnMultiSelectFilter values={urgencyF} onChange={setUrgencyF}
                    options={URGENCY_OPTIONS} allLabel="Any" ariaLabel="Filter by urgency" />
                </Table.Th>
                <Table.Th>
                  <ColumnTextFilter value={descQ} onChange={setDescQ}
                    placeholder="Search description…" ariaLabel="Filter by description" />
                </Table.Th>
                <Table.Th>
                  <AgreementPicker
                    size="xs"
                    placeholder="All tasks"
                    value={relatedTo}
                    onChange={(v) => setRelatedTo(v)}
                    pinnedOptions={[
                      { value: '__all__', label: 'All' },
                      { value: '__general__', label: 'General (no agreement)' },
                    ]}
                  />
                </Table.Th>
                <Table.Th>
                  <ColumnMultiSelectFilter values={rmF} onChange={setRmF}
                    options={assigneeOptions} allLabel="Any RM" ariaLabel="Filter by RM" />
                </Table.Th>
                <Table.Th>
                  <ColumnMultiSelectFilter values={assigneeF} onChange={setAssigneeF}
                    options={assigneeOptions} allLabel="Anyone" ariaLabel="Filter by assignee" />
                </Table.Th>
                {/* Status cell stays filter-free (population chips own that
                    axis) — it hosts the Clear button instead. */}
                <Table.Th>
                  {filtersActive && (
                    <Tooltip label="Clear all column filters" withArrow>
                      <Button size="compact-xs" variant="subtle" color="gray"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear
                      </Button>
                    </Tooltip>
                  )}
                </Table.Th>
                <Table.Th>
                  <ColumnMultiSelectFilter values={createdByF} onChange={setCreatedByF}
                    options={assigneeOptions} allLabel="Anyone" ariaLabel="Filter by creator" />
                </Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {!q.isLoading && items.length === 0 && filtersActive && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No tasks match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              )}
              {items.map((t) => {
                const overdue = isOverdue(t);
                const isDeleted = !!t.deleted_at;
                return (
                  <Table.Tr key={t.id}
                    style={isDeleted ? { opacity: 0.55 } : undefined}>
                    <Table.Td>
                      <Text size="sm" ff="monospace"
                        c={overdue ? 'red' : undefined}
                        fw={overdue ? 600 : undefined}
                        td={isDeleted ? 'line-through' : undefined}>
                        {t.deadline}
                      </Text>
                      {overdue && (
                        <Text size="xs" c="red" fw={500}>Overdue</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={urgencyColor(t.urgency)} variant="light">
                        {urgencyLabel(t.urgency)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" td={isDeleted ? 'line-through' : undefined}>
                        {t.description}
                      </Text>
                      {isDeleted && (
                        <StatusExplained entity="task" status="DELETED">
                          <Badge color="red" variant="light" size="sm" mt={4}>Deleted</Badge>
                        </StatusExplained>
                      )}
                      <PendingCRBadge crs={t.pending_change_requests} />
                    </Table.Td>
                    <Table.Td>
                      {t.agreement_id ? (
                        <Anchor component={Link} to={`/agreements/${t.agreement_id}?tab=tasks`} size="sm">
                          {/* The server-resolved label (deleted agreements included);
                              no per-row lookup, which showed "unresolved" (2026-10-16). */}
                          <EntityFriendlyLabel entityType="agreements" entityId={t.agreement_id}
                            preResolvedLabel={t.agreement_label ?? undefined} />
                        </Anchor>
                      ) : (
                        <Badge variant="light" color="gray">General</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{t.rm_label ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{t.assignee_label ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <StatusExplained entity="task" status={t.status}>
                        <Badge color={STATUS_COLOR[t.status]} variant="light">
                          {AGREEMENT_TASK_STATUS_LABELS[t.status] ?? t.status}
                        </Badge>
                      </StatusExplained>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">{t.created_by_label ?? '—'}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {t.created_at.substring(0, 10)}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <TaskRowActions task={t} onEdit={openEdit} />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        {q.data && q.data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{q.data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}

        <TaskFormModal
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
          editing={editing}
        />
      </Stack>
    </Container>
  );
}
